import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, JSX } from 'react';
import { Confirm, useRecordContext, usePermissions } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    LinearProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Clear, Refresh, Search, VerticalAlignBottom } from '@mui/icons-material';
import { permissionManager } from '../../../permissions/manager';
import { eyebrow, numeric } from '../../../dashboard/components/styles/dashboard-tokens';
import { describeAge } from '../../../queues/queuesApi';
import { Operator } from '../../../../types/entities';
import {
    LogFile,
    LogOutput,
    MAX_HELD_BYTES,
    TAIL_BYTES,
    canLoadEarlier,
    clearLogFile,
    describeBytes,
    loadLogFiles,
    loadLogOutput,
    prependEarlier,
    secondsSince,
} from './operatorLogsApi';

/**
 * The Logs tab on the Operator screen.
 *
 * **Rebuilt 2026-09-21 at the user's request**, along with the Operator routes behind it. What it
 * was: a 1/3-width column of bare filenames in filesystem order, a hardcoded `#332fe1` for the
 * selected one that ignored the theme, a 400px box with a 100px bottom margin, and a `<pre>`
 * showing a file from the top. It carried an `outputSection` state that nothing could change and
 * that the Operator ignored anyway.
 *
 * Three things drive the design, and all three come from the routes now carrying real information:
 *
 * - **Sizes are shown, because one of these files is 287 MB and the rest are a few KB.** Nothing on
 *   the old screen said which was which, and opening the big one would have taken the Operator with
 *   it.
 * - **The output is the tail, so it is scrolled to the bottom.** A log is read to find out what just
 *   happened. The old panel put the oldest line at the top and left the reader to scroll.
 * - **The list is filterable**, because there are 48 files on the development box and picking one
 *   out of an unsorted column was the actual complaint.
 *
 * **Reading further back** was added the same day at the user's request: a file opens on its last
 * 256 KB as before, and "Load more" puts the 256 KB above it on top, as many times as
 * wanted up to `MAX_HELD_BYTES`. Each chunk is asked for by the byte offset the held text starts at,
 * so nothing is fetched twice and the seams are exact.
 */

/** Both panes share a height so they line up, and each scrolls on its own. */
const PANE_HEIGHT: number = 560;

const pane = {
    border: 1,
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'background.paper',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
} as const;

const mono = { ...numeric, fontSize: 13 } as const;

/**
 * One file in the list.
 *
 * Selection is a background and a left edge from the theme, not a text colour: the old panel said
 * which file was open with `#332fe1` alone, which is a hardcoded blue that ignores light and dark
 * both, and colour on its own is not something everyone can see.
 */
const FileRow = ({ file, selected, onPick }: {
    file: LogFile,
    selected: boolean,
    onPick: (name: string) => void,
}): JSX.Element => (
    <Box
        component="button"
        type="button"
        onClick={ (): void => onPick(file.name) }
        aria-current={ selected ? 'true' : undefined }
        sx={ {
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            border: 0,
            borderLeft: 3,
            borderLeftStyle: 'solid',
            borderLeftColor: selected ? 'primary.main' : 'transparent',
            bgcolor: selected ? 'action.selected' : 'transparent',
            px: 1.5,
            py: 1,
            display: 'block',
            '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
            '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
        } }
    >
        <Typography sx={ { ...mono, fontWeight: selected ? 600 : 400, wordBreak: 'break-all' } }>
            { file.name }
        </Typography>
        <Typography variant="caption" color="text.secondary">
            { describeBytes(file.size) + ' · ' + describeAge(secondsSince(file.modified)) }
        </Typography>
    </Box>
);

export const OperatorLogs: () => JSX.Element = (): JSX.Element => {
    const record: Operator = useRecordContext();
    const { permissions } = usePermissions();
    // Truncating a log file changes the Operator's state without removing the Operator, so `edit`.
    const canClearLogs: boolean = permissionManager.isAllowed(permissions, 'HQ_System::operators.edit');

    const [files, setFiles] = useState<LogFile[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [active, setActive] = useState<string | null>(null);
    const [output, setOutput] = useState<LogOutput | null>(null);
    const [loadingFiles, setLoadingFiles] = useState<boolean>(true);
    const [loadingOutput, setLoadingOutput] = useState<boolean>(false);
    const [loadingEarlier, setLoadingEarlier] = useState<boolean>(false);
    const [problem, setProblem] = useState<string>('');
    const [confirmingClear, setConfirmingClear] = useState<boolean>(false);

    const operatorId: number | string | undefined = record?.id;
    const outputBox = useRef<HTMLPreElement | null>(null);
    // What is on screen now, for a load that finishes after the reader has moved on.
    const latest = useRef<LogOutput | null>(null);
    latest.current = output;
    // The scroll height just before an earlier chunk went in on top, so the reader's place survives.
    const heightBeforePrepend = useRef<number | null>(null);

    const readFiles = useCallback(async (): Promise<void> => {
        if (operatorId === undefined) {
            return;
        }
        setLoadingFiles(true);
        try {
            setFiles(await loadLogFiles(operatorId));
            setProblem('');
        } catch (unreachable: any) {
            // **Shown, not logged.** Every failure here used to go to `console.log` and leave the
            // panel on its loading line, so an Operator that was down looked like a slow one.
            setProblem(unreachable?.message ?? 'The Operator could not be reached.');
        } finally {
            setLoadingFiles(false);
        }
    }, [operatorId]);

    const readOutput = useCallback(async (file: string): Promise<void> => {
        if (operatorId === undefined) {
            return;
        }
        setLoadingOutput(true);
        setActive(file);
        try {
            setOutput(await loadLogOutput(operatorId, file, TAIL_BYTES));
            setProblem('');
        } catch (unreachable: any) {
            setOutput(null);
            setProblem(unreachable?.message ?? 'That log could not be read.');
        } finally {
            setLoadingOutput(false);
        }
    }, [operatorId]);

    /**
     * Put the stretch above what is held on top of it.
     *
     * Checked against `latest` when it lands, because the reader may have opened another file,
     * refreshed, or emptied this one in the meantime, and a chunk joined onto the wrong text would
     * show a log that never existed. A chunk that does not meet the held text means the file changed
     * underneath, which is said rather than papered over.
     */
    const readEarlier = async (): Promise<void> => {
        const held: LogOutput | null = output;
        if (operatorId === undefined || held === null || held.start === null) {
            return;
        }
        setLoadingEarlier(true);
        try {
            const earlier: LogOutput = await loadLogOutput(operatorId, held.file, TAIL_BYTES, held.start);
            if (latest.current !== held) {
                return;
            }
            const joined: LogOutput | null = prependEarlier(held, earlier);
            if (joined === null) {
                setProblem('This log has changed since it was opened, so the earlier part no longer lines up. Refresh it to read it again.');

                return;
            }
            heightBeforePrepend.current = outputBox.current?.scrollHeight ?? null;
            setOutput(joined);
            setProblem('');
        } catch (unreachable: any) {
            setProblem(unreachable?.message ?? 'The earlier part of that log could not be read.');
        } finally {
            setLoadingEarlier(false);
        }
    };

    useEffect((): void => { void readFiles(); }, [readFiles]);

    /**
     * Put the newest lines in view, or keep the reader's place after loading earlier ones.
     *
     * The answer is the end of the file, so the interesting line is the last one. Left at the top,
     * a reader opens a log and sees whatever happened first, which on a busy file is days ago.
     *
     * After "Load earlier" the opposite: the new text goes in above, and without moving the scroll
     * by exactly its height the line being read would jump away. A layout effect, so the adjustment
     * lands before the browser paints the jump.
     */
    useLayoutEffect((): void => {
        const box: HTMLPreElement | null = outputBox.current;
        if (box === null || output === null) {
            return;
        }
        if (heightBeforePrepend.current !== null) {
            box.scrollTop += box.scrollHeight - heightBeforePrepend.current;
            heightBeforePrepend.current = null;

            return;
        }
        box.scrollTop = box.scrollHeight;
    }, [output]);

    const shown: LogFile[] = useMemo((): LogFile[] => {
        const needle: string = filter.trim().toLowerCase();

        return needle === ''
            ? files
            : files.filter((one): boolean => one.name.toLowerCase().includes(needle));
    }, [files, filter]);

    const scrollToEnd = (): void => {
        if (outputBox.current !== null) {
            outputBox.current.scrollTop = outputBox.current.scrollHeight;
        }
    };

    const emptyActiveLog = async (): Promise<void> => {
        setConfirmingClear(false);
        if (operatorId === undefined || active === null) {
            return;
        }
        setLoadingOutput(true);
        try {
            await clearLogFile(operatorId, active);
            await readOutput(active);
            await readFiles();
        } catch (refused: any) {
            setProblem(refused?.message ?? 'That log could not be emptied.');
            setLoadingOutput(false);
        }
    };

    return (
        <Box sx={ { width: '100%' } }>
            { problem !== '' && <Alert severity="error" sx={ { mb: 2 } }>{ problem }</Alert> }

            <Stack direction={ { xs: 'column', md: 'row' } } spacing={ 2 } alignItems="stretch">
                <Box sx={ { ...pane, width: { xs: '100%', md: 320 }, flexShrink: 0, height: PANE_HEIGHT } }>
                    <Box sx={ { p: 1.5, borderBottom: 1, borderColor: 'divider' } }>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={ eyebrow }>
                                { 'Log files' + (files.length > 0 ? ' · ' + files.length : '') }
                            </Typography>
                            <IconButton size="small" title="Refresh the file list" onClick={ (): void => { void readFiles(); } }>
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Stack>
                        { /* 48 files on the development box, and picking one out of an unsorted
                             column was the complaint this panel was rebuilt for. */ }
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Filter"
                            value={ filter }
                            onChange={ (event): void => setFilter(event.target.value) }
                            sx={ { mt: 1 } }
                            slotProps={ {
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            } }
                        />
                    </Box>

                    { loadingFiles && <LinearProgress /> }

                    <Box sx={ { overflowY: 'auto', flex: 1 } }>
                        { !loadingFiles && shown.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={ { p: 2 } }>
                                { files.length === 0 ? 'No log files.' : 'No file matches that.' }
                            </Typography>
                        ) }
                        { shown.map((file): JSX.Element => (
                            <FileRow
                                key={ file.name }
                                file={ file }
                                selected={ file.name === active }
                                onPick={ (name): void => { void readOutput(name); } }
                            />
                        )) }
                    </Box>
                </Box>

                <Box sx={ { ...pane, flex: 1, minWidth: 0, height: PANE_HEIGHT } }>
                    <Box sx={ { p: 1.5, borderBottom: 1, borderColor: 'divider' } }>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={ 1 }>
                            <Box sx={ { minWidth: 0 } }>
                                <Typography sx={ { ...mono, fontWeight: 600, wordBreak: 'break-all' } }>
                                    { active ?? 'No log selected' }
                                </Typography>
                                { output !== null && (
                                    <Typography variant="caption" color="text.secondary">
                                        { [
                                            output.truncated
                                                ? 'last ' + describeBytes(output.returned) + ' of ' + describeBytes(output.size)
                                                : describeBytes(output.size),
                                            'written ' + describeAge(secondsSince(output.modified)),
                                        ].join(' · ') }
                                    </Typography>
                                ) }
                            </Box>
                            { active !== null && (
                                <Stack direction="row" spacing={ 0.5 } sx={ { flexShrink: 0 } }>
                                    <IconButton size="small" title="Jump to the newest lines" onClick={ scrollToEnd }>
                                        <VerticalAlignBottom fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" title="Refresh this log" onClick={ (): void => { void readOutput(active); } }>
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                    { canClearLogs && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            title="Empty this log file"
                                            onClick={ (): void => {
                                                // HQ-50: focus left on this button is hidden while the dialog is open.
                                                (document.activeElement as HTMLElement|null)?.blur();
                                                setConfirmingClear(true);
                                            } }
                                        >
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    ) }
                                </Stack>
                            ) }
                        </Stack>
                    </Box>

                    { (loadingOutput || loadingEarlier) && <LinearProgress /> }

                    { output !== null && output.truncated && (
                        <Alert
                            severity="info"
                            square
                            sx={ { borderRadius: 0, alignItems: 'center' } }
                            action={ canLoadEarlier(output) && (
                                <Button
                                    size="small"
                                    color="inherit"
                                    disabled={ loadingOutput || loadingEarlier }
                                    onClick={ (): void => { void readEarlier(); } }
                                >
                                    { 'Load more' }
                                </Button>
                            ) }
                        >
                            { 'Showing the last ' + describeBytes(output.returned) + ' of ' + describeBytes(output.size) + '.' }
                            { output.returned >= MAX_HELD_BYTES
                                && ' That is as much as this panel holds; the rest of the file has to be copied off the Operator.' }
                        </Alert>
                    ) }

                    <Box
                        component="pre"
                        ref={ outputBox }
                        tabIndex={ 0 }
                        sx={ {
                            ...mono,
                            flex: 1,
                            overflow: 'auto',
                            m: 0,
                            p: 2,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: 1.5,
                        } }
                    >
                        { active === null && (
                            <Typography variant="body2" color="text.secondary">
                                { 'Pick a log file to read it.' }
                            </Typography>
                        ) }
                        { active !== null && output !== null && output.output === '' && (
                            <Typography variant="body2" color="text.secondary">
                                { 'This log is empty.' }
                            </Typography>
                        ) }
                        { output?.output }
                    </Box>
                </Box>
            </Stack>

            <Confirm
                isOpen={ confirmingClear }
                loading={ loadingOutput }
                title="Empty this log file?"
                content={ (active ?? 'This log') + ' will be emptied on the Operator. This cannot be undone.' }
                onConfirm={ (): void => { void emptyActiveLog(); } }
                onClose={ (): void => setConfirmingClear(false) }
            />
        </Box>
    );
}
